import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Camera, 
  GitCompare, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  X,
  ChevronDown,
  ChevronUp,
  Calendar
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metric display names for better readability
const METRIC_LABELS = {
  // Image Monitor
  total_images: "Total Images",
  pages_with_images: "Pages with Images",
  total_pages: "Total Pages",
  // SEO/MetaAlt Monitor
  missing_title: "Missing Title",
  missing_description: "Missing Description",
  missing_alt: "Missing Alt Text",
  oversized_images: "Oversized Images",
  // MetaAlt Quality
  quality_score: "Quality Score",
  pages_scanned: "Pages Scanned",
  alt_issues_count: "Alt Text Issues",
  title_issues_count: "Title Issues",
  desc_issues_count: "Description Issues",
  // OnPage Monitor
  low_content_count: "Low Content Pages",
  medium_content_count: "Medium Content Pages",
  good_content_count: "Good Content Pages",
  excellent_content_count: "Excellent Content Pages",
  avg_eeat_score: "Avg EEAT Score",
  avg_hcu_score: "Avg HCU Score",
  pages_with_faq: "Pages with FAQ",
  pages_without_faq: "Pages without FAQ",
  low_internal_links_count: "Low Internal Links",
  pages_with_schema: "Pages with Schema",
  pages_without_schema: "Pages without Schema",
  // Section Monitor
  avg_section_score: "Avg Section Score",
  avg_sections_present: "Avg Sections Present",
  // PageSpeed Monitor
  avg_performance_score: "Avg Performance",
  fast_pages: "Fast Pages",
  average_pages: "Average Pages",
  slow_pages: "Slow Pages",
  avg_fcp: "Avg FCP",
  avg_lcp: "Avg LCP",
  avg_cls: "Avg CLS",
  avg_tbt: "Avg TBT",
  // TechSEO Monitor
  health_score: "Health Score",
  total_issues: "Total Issues",
  critical_issues: "Critical Issues",
  warning_issues: "Warning Issues",
  info_issues: "Info Issues",
  pages_analyzed: "Pages Analyzed"
};

// Metrics where lower is better (for determining improvement direction)
const LOWER_IS_BETTER = [
  "missing_title", "missing_description", "missing_alt", "oversized_images",
  "total_issues", "critical_issues", "warning_issues", "info_issues",
  "low_content_count", "alt_issues_count", "title_issues_count", "desc_issues_count",
  "slow_pages", "pages_without_faq", "low_internal_links_count", "pages_without_schema",
  "avg_fcp", "avg_lcp", "avg_cls", "avg_tbt"
];

export const SnapshotCompare = ({ siteId, siteName, monitorType, buttonClassName = "" }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [selectedSnapshots, setSelectedSnapshots] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapshotName, setSnapshotName] = useState("");

  useEffect(() => {
    if (showModal && siteId) {
      fetchSnapshots();
    }
  }, [showModal, siteId]);

  const fetchSnapshots = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/snapshots/${monitorType}/${siteId}`);
      setSnapshots(response.data);
    } catch (error) {
      console.error("Failed to fetch snapshots:", error);
    }
  };

  const createSnapshot = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/snapshots`, {
        site_id: siteId,
        monitor_type: monitorType,
        name: snapshotName || null
      });
      toast.success("Snapshot created successfully");
      setSnapshotName("");
      fetchSnapshots();
    } catch (error) {
      toast.error("Failed to create snapshot");
    }
    setLoading(false);
  };

  const deleteSnapshot = async (snapshotId) => {
    try {
      await axios.delete(`${API_URL}/api/snapshots/${snapshotId}`);
      toast.success("Snapshot deleted");
      fetchSnapshots();
      setSelectedSnapshots(prev => prev.filter(id => id !== snapshotId));
    } catch (error) {
      toast.error("Failed to delete snapshot");
    }
  };

  const toggleSnapshotSelection = (snapshotId) => {
    setSelectedSnapshots(prev => {
      if (prev.includes(snapshotId)) {
        return prev.filter(id => id !== snapshotId);
      }
      if (prev.length >= 2) {
        return [prev[1], snapshotId];
      }
      return [...prev, snapshotId];
    });
  };

  const compareSnapshots = async () => {
    if (selectedSnapshots.length !== 2) {
      toast.error("Please select exactly 2 snapshots to compare");
      return;
    }
    
    await performComparison(selectedSnapshots[0], selectedSnapshots[1]);
  };

  const performComparison = async (snap1Id, snap2Id) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/snapshots/${monitorType}/${siteId}/compare`,
        { params: { snapshot1_id: snap1Id, snapshot2_id: snap2Id } }
      );
      setComparison(response.data);
      setShowCompare(true);
    } catch (error) {
      toast.error("Failed to compare snapshots");
    }
    setLoading(false);
  };

  const quickCompareToBaseline = async () => {
    const baseline = snapshots.find(s => s.name === "Initial Baseline");
    const latest = snapshots.find(s => s.name !== "Initial Baseline");
    if (baseline && latest && baseline.id !== latest.id) {
      setSelectedSnapshots([baseline.id, latest.id]);
      await performComparison(baseline.id, latest.id);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeIcon = (change) => {
    if (!change || change.diff === undefined) return <Minus className="w-4 h-4 text-zinc-500" />;
    if (change.diff === 0) return <Minus className="w-4 h-4 text-zinc-500" />;
    if (change.improved) return <TrendingUp className="w-4 h-4 text-green-500" />;
    return <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  const getChangeColor = (change) => {
    if (!change || change.diff === undefined || change.diff === 0) return "text-zinc-400";
    return change.improved ? "text-green-400" : "text-red-400";
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1.5 ${buttonClassName}`}
        data-testid="snapshot-compare-btn"
      >
        <GitCompare className="w-4 h-4" />
        Compare
      </Button>

      {/* Main Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-blue-400" />
                  Snapshot Comparison
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">{siteName}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Create new snapshot */}
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Create New Snapshot
                </h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={snapshotName}
                    onChange={(e) => setSnapshotName(e.target.value)}
                    placeholder="Snapshot name (optional)"
                    className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-300 placeholder-zinc-600"
                  />
                  <Button
                    onClick={createSnapshot}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    Capture
                  </Button>
                </div>
              </div>

              {/* Existing snapshots */}
              <div>
                <h3 className="text-sm font-medium text-zinc-300 mb-3">
                  Saved Snapshots ({snapshots.length})
                </h3>
                
                {/* Quick Compare to Baseline */}
                {snapshots.length >= 2 && snapshots.some(s => s.name === "Initial Baseline") && (
                  <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-400">Quick Compare to Initial Baseline</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Compare current state against your initial baseline</p>
                      </div>
                      <Button
                        onClick={quickCompareToBaseline}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-sm"
                      >
                        <GitCompare className="w-4 h-4 mr-1" />
                        Compare Now
                      </Button>
                    </div>
                  </div>
                )}
                
                {snapshots.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-8">
                    No snapshots yet. Create your first snapshot to track progress over time.
                  </p>
                ) : snapshots.length === 1 && snapshots[0].name === "Initial Baseline" ? (
                  <div className="text-center py-6">
                    <div className="inline-flex items-center gap-2 px-3 py-2 bg-blue-900/30 border border-blue-700/50 rounded-lg mb-3">
                      <Camera className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-300">Initial Baseline captured automatically</span>
                    </div>
                    <p className="text-zinc-500 text-sm">
                      Create a new snapshot after making improvements to compare progress.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {snapshots.map((snapshot) => (
                      <div
                        key={snapshot.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          selectedSnapshots.includes(snapshot.id)
                            ? "bg-blue-600/20 border-blue-500"
                            : "bg-zinc-800/30 border-zinc-700 hover:border-zinc-600"
                        }`}
                        onClick={() => toggleSnapshotSelection(snapshot.id)}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedSnapshots.includes(snapshot.id)
                            ? "border-blue-500 bg-blue-500"
                            : "border-zinc-600"
                        }`}>
                          {selectedSnapshots.includes(snapshot.id) && (
                            <span className="text-white text-xs font-bold">
                              {selectedSnapshots.indexOf(snapshot.id) + 1}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-200">{snapshot.name}</p>
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(snapshot.created_at)}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-zinc-400">
                          {Object.entries(snapshot.snapshot_data).slice(0, 2).map(([key, val]) => (
                            <span key={key} className="bg-zinc-800 px-2 py-1 rounded">
                              {METRIC_LABELS[key] || key}: {typeof val === 'number' ? val.toLocaleString() : val}
                            </span>
                          ))}
                        </div>
                        
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSnapshot(snapshot.id); }}
                          className="text-zinc-500 hover:text-red-400 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-700 flex justify-between">
              <p className="text-xs text-zinc-500">
                {selectedSnapshots.length === 2 
                  ? "Ready to compare" 
                  : `Select ${2 - selectedSnapshots.length} more snapshot${2 - selectedSnapshots.length !== 1 ? 's' : ''} to compare`
                }
              </p>
              <Button
                onClick={compareSnapshots}
                disabled={selectedSnapshots.length !== 2 || loading}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <GitCompare className="w-4 h-4 mr-1" />
                Compare Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Results Modal */}
      {showCompare && comparison && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <div>
                <h2 className="text-lg font-semibold text-white">Comparison Results</h2>
                <p className="text-xs text-zinc-500">
                  {comparison.snapshot1.name} → {comparison.snapshot2.name}
                </p>
              </div>
              <button onClick={() => setShowCompare(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Comparison Table */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-4 gap-2 text-xs font-medium text-zinc-500 mb-2 px-2">
                <div>Metric</div>
                <div className="text-center">Before</div>
                <div className="text-center">After</div>
                <div className="text-center">Change</div>
              </div>
              
              <div className="space-y-1">
                {Object.entries(comparison.changes).map(([key, change]) => (
                  <div 
                    key={key} 
                    className="grid grid-cols-4 gap-2 items-center p-2 rounded bg-zinc-800/30 hover:bg-zinc-800/50"
                  >
                    <div className="text-sm text-zinc-300">
                      {METRIC_LABELS[key] || key}
                    </div>
                    <div className="text-center text-sm text-zinc-400 font-mono">
                      {typeof change.before === 'number' ? change.before.toLocaleString() : change.before || '-'}
                    </div>
                    <div className="text-center text-sm text-zinc-200 font-mono font-medium">
                      {typeof change.after === 'number' ? change.after.toLocaleString() : change.after || '-'}
                    </div>
                    <div className={`text-center text-sm font-mono flex items-center justify-center gap-1 ${getChangeColor(change)}`}>
                      {getChangeIcon(change)}
                      {change.diff !== undefined && (
                        <span>
                          {change.diff > 0 ? '+' : ''}{change.diff.toLocaleString()}
                          {change.pct_change !== undefined && change.pct_change !== 0 && (
                            <span className="text-xs ml-1">({change.pct_change > 0 ? '+' : ''}{change.pct_change}%)</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="p-4 border-t border-zinc-700 bg-zinc-800/30">
              <div className="flex items-center justify-between text-sm">
                <div className="text-zinc-400">
                  <span className="text-zinc-500">Period:</span>{' '}
                  {formatDate(comparison.snapshot1.created_at)} → {formatDate(comparison.snapshot2.created_at)}
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowCompare(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SnapshotCompare;
